import React, { createContext, useContext, useEffect, useState } from "react";

export const translations = {
  en: {
    nav: { about: "How I Think", approach: "Approach", build: "What I Build", projects: "Projects", tech: "Stack", contact: "Contact", cta: "Let's talk" },
    hero: {
      badge: "AI Business Consultant",
      line1: "I build AI systems",
      line2: "that transform",
      line3: "businesses.",
      subTitle: "Not websites. Not software. Business systems.",
      sub: "I combine commercial strategy, AI and rapid software development to solve operational problems that traditional software companies don't even notice.",
      cta1: "Let's build something impossible",
      cta2: "View selected projects",
      scroll: "Scroll to explore",
    },
    about: {
      overline: "How I think",
      title: "Not a biography. A way of seeing problems.",
      lead: "I move between the boardroom and the factory floor. Where most see a feature request, I see a process waiting to be rethought.",
      items: [
        { t: "Commercial Background", d: "Years building pipelines, pricing and customer journeys. I understand how companies actually make money." },
        { t: "Industrial Manufacturing", d: "Time spent inside production lines, planning and quality. I know where the real bottlenecks hide." },
        { t: "Software", d: "Full-stack execution. I ship production systems, not slide decks." },
        { t: "Artificial Intelligence", d: "Every problem starts with the question: where does AI create disproportionate leverage?" },
        { t: "Business Strategy", d: "Technology is only useful if it moves a number that matters to the business." },
        { t: "Continuous Improvement", d: "Lean thinking. Remove waste, compound small gains, measure everything." },
        { t: "Automation", d: "If a human does it twice the same way, it should be a system." },
        { t: "Product Design", d: "Interfaces people actually want to use, built around how work really happens." },
      ],
    },
    approach: {
      overline: "My approach",
      title: "From operational chaos to AI-powered clarity.",
      steps: [
        { t: "Discover", d: "Understand the business, not the brief." },
        { t: "Challenge assumptions", d: "The stated problem is rarely the real one." },
        { t: "Map operations", d: "Trace every step, handoff and delay." },
        { t: "Design AI workflows", d: "Find where intelligence creates leverage." },
        { t: "Prototype", d: "Working software in days, not quarters." },
        { t: "Deploy", d: "Ship into the real world, with real users." },
        { t: "Measure", d: "Tie everything back to profit, speed, experience." },
        { t: "Improve", d: "Iterate relentlessly. The system gets smarter." },
      ],
    },
    build: {
      overline: "What I build",
      title: "End-to-end systems that change how companies operate.",
      cards: [
        { t: "Project Management for Developers & Constructors", d: "Plan, track and deliver projects across development and construction teams.", k: "platform" },
        { t: "Industrial Manufacturing", d: "Factory floor control, production planning, quality.", k: "factory" },
        { t: "Healthcare Management", d: "Hospital operations, operating rooms, scheduling.", k: "health" },
        { t: "Warehouse & Fulfilment", d: "Picking, packing, barcode, shipping, integrations.", k: "warehouse" },
        { t: "E-commerce Platforms", d: "Storefronts wired to inventory, payments and ops.", k: "ecom" },
        { t: "Sports Club Platforms", d: "Parents, academies, events, payments, attendance.", k: "sports" },
        { t: "Custom CRM & ERP", d: "Extensions and bespoke systems around NetSuite.", k: "crm" },
        { t: "AI Agents & Automation", d: "Agents that read, decide and act across your stack.", k: "agent" },
      ],
      integrations: ["NetSuite", "Shopify", "Stripe", "OpenAI", "Claude", "Gemini", "Google Workspace", "Microsoft"],
    },
    transformation: {
      overline: "Interactive",
      title: "Drag your business from chaos to AI operations.",
      sub: "Pull the handle. Watch what happens when operations stop being reactive and start thinking.",
      chaos: "Chaos",
      ai: "AI-powered",
      before: [
        { l: "Manual data entry", v: "14h / week" },
        { l: "Order errors", v: "8.2%" },
        { l: "Decision latency", v: "3 days" },
        { l: "Visibility", v: "Spreadsheets" },
      ],
      after: [
        { l: "Manual data entry", v: "0.5h / week" },
        { l: "Order errors", v: "0.4%" },
        { l: "Decision latency", v: "Real-time" },
        { l: "Visibility", v: "Live dashboard" },
      ],
      dragHint: "Drag",
    },
    projects: {
      overline: "Featured projects",
      title: "Realistic systems. Real operational impact.",
      open: "Explore case",
      close: "Close",
      challenge: "The challenge",
      solution: "What I built",
      impact: "Impact",
      list: [
        {
          name: "Hospital Operations Suite",
          tagline: "Operating rooms, staff & equipment, orchestrated.",
          tags: ["Healthcare", "Scheduling", "Realtime"],
          challenge: "A private hospital lost operating-room hours to clashing schedules, missing equipment and manual coordination across teams.",
          solution: "A unified suite for OR scheduling, staff rostering, equipment tracking and inventory, with conflict detection and live theatre status boards.",
          features: ["Operating room scheduling", "Staff & shift management", "Equipment tracking", "Inventory & sterilisation", "Conflict detection", "Live status boards"],
          impact: [{ l: "OR utilisation", v: "+27%" }, { l: "Scheduling conflicts", v: "-91%" }, { l: "Setup delays", v: "-40%" }],
        },
        {
          name: "Industrial Textile Platform",
          tagline: "From raw components to shipped product, in one view.",
          tags: ["Manufacturing", "Planning", "Quality"],
          challenge: "A textile manufacturer ran production on whiteboards and spreadsheets, with no real-time view of capacity, quality or components.",
          solution: "A factory management platform covering production orders, component planning, quality control and real-time floor dashboards.",
          features: ["Production planning", "Factory floor control", "Quality management", "Component & BOM tracking", "Capacity planning", "Real-time dashboards"],
          impact: [{ l: "On-time delivery", v: "+34%" }, { l: "Scrap rate", v: "-22%" }, { l: "Planning time", v: "-70%" }],
        },
        {
          name: "Warehouse Management System",
          tagline: "Fulfilment that scales without chaos.",
          tags: ["Logistics", "Fulfilment", "Integrations"],
          challenge: "A growing e-commerce operation hit a wall: mispicks, slow packing and no integration between store, stock and couriers.",
          solution: "A WMS with barcode-driven picking, packing validation, shipping label automation and deep Shopify + courier integrations.",
          features: ["Barcode picking", "Packing validation", "Shipping automation", "Inventory sync", "Packing lists", "Courier integrations"],
          impact: [{ l: "Picks per hour", v: "+58%" }, { l: "Mispick rate", v: "-86%" }, { l: "Orders / day", v: "3.2x" }],
        },
        {
          name: "Sports Club Platform",
          tagline: "Academies, parents and payments in one place.",
          tags: ["Platform", "Payments", "Community"],
          challenge: "A multi-sport academy juggled registrations, attendance, events and parent payments across disconnected tools.",
          solution: "A platform for parents and academies covering scheduling, attendance, events, an online store and recurring payments.",
          features: ["Parent portal", "Academy management", "Event scheduling", "Attendance tracking", "Online store", "Recurring payments"],
          impact: [{ l: "Admin time", v: "-65%" }, { l: "Payment collection", v: "+41%" }, { l: "Parent NPS", v: "72" }],
        },
        {
          name: "Event & Venue Platform",
          tagline: "Reservations and operations, choreographed.",
          tags: ["Operations", "Reservations", "Venue"],
          challenge: "A venue group managed reservations, resources and event-day operations through phone calls and paper run-sheets.",
          solution: "A venue management platform handling reservations, resource allocation and live operational run-sheets for event days.",
          features: ["Venue management", "Reservations engine", "Resource allocation", "Operations run-sheets", "Capacity control", "Live timelines"],
          impact: [{ l: "Double-bookings", v: "0" }, { l: "Setup errors", v: "-78%" }, { l: "Revenue / venue", v: "+19%" }],
        },
      ],
    },
    different: {
      overline: "What makes me different",
      title: "I don't write code. I identify opportunities.",
      cards: [
        { t: "Commercial Vision", d: "I understand sales pipelines, business models and customer journeys — then build for them." },
        { t: "Industrial Experience", d: "Years inside manufacturing and production. I've seen where systems break in the real world." },
        { t: "AI First", d: "Every solution starts by asking where AI creates leverage, not where it looks impressive." },
        { t: "Rapid Execution", d: "Ideas become working software in days. Momentum is a competitive advantage." },
        { t: "Business Thinking", d: "Software is only useful if it improves profit, speed or customer experience." },
      ],
    },
    tech: {
      overline: "Technology",
      title: "The stack I orchestrate.",
      sub: "Tools are means, not ends — chosen to fit the problem.",
      items: ["Claude", "OpenAI", "Gemini", "Supabase", "Postgres", "Next.js", "React", "Node", "TypeScript", "Python", "Stripe", "Shopify", "NetSuite", "Google Cloud", "Vercel", "WebSockets", "Realtime", "APIs", "OCR", "LLMs", "Automation"],
    },
    stats: {
      overline: "By the numbers",
      title: "Outcomes, not output.",
      items: [
        { v: 36, suffix: "", l: "Projects delivered" },
        { v: 11, suffix: "", l: "Businesses improved" },
        { v: 80, suffix: "+", l: "Odoo modules" },
        { v: 210, suffix: "+", l: "Processes automated" },
        { v: 3, suffix: "", l: "Countries" },
        { v: 7, suffix: "", l: "Industries" },
      ],
    },
    testimonials: {
      overline: "Testimonials",
      title: "What partners say.",
      list: [
        { q: "Rui didn't build what we asked for. He built what we actually needed — and our operations have never been the same.", n: "Operations Director", r: "Industrial Manufacturing" },
        { q: "In two weeks he understood our business better than vendors we'd worked with for years. The system paid for itself in a quarter.", n: "CEO", r: "Healthcare Group" },
        { q: "Most developers ship features. Rui ships outcomes. He thinks like an owner, not a contractor.", n: "Founder", r: "E-commerce Platform" },
        { q: "He walked our factory floor before writing a single line of code. That's why it worked.", n: "Plant Manager", r: "Textile Production" },
      ],
    },
    contact: {
      overline: "Contact",
      line1: "If you're looking for another software company, I'm probably not the right choice.",
      line2: "If you're looking for someone who will rethink your business from the ground up, let's talk.",
      cta: "Book a Discovery Call",
      email: "Or email me directly",
      waMsg: "Hi Rui, I'd like to talk about a project.",
    },
    footer: {
      tagline: "AI business consultant. I rethink how companies operate.",
      rights: "All rights reserved.",
      built: "Designed & built with intent.",
    },
  },
  pt: {
    nav: { about: "Como Penso", approach: "Abordagem", build: "O Que Construo", projects: "Projetos", tech: "Stack", contact: "Contacto", cta: "Vamos falar" },
    hero: {
      badge: "Consultor de Negócio com IA",
      line1: "Construo sistemas de IA",
      line2: "que transformam",
      line3: "negócios.",
      subTitle: "Não são websites. Não é software. São sistemas de negócio.",
      sub: "Combino estratégia comercial, IA e desenvolvimento rápido de software para resolver problemas operacionais que as empresas de software tradicionais nem sequer notam.",
      cta1: "Vamos construir algo impossível",
      cta2: "Ver projetos selecionados",
      scroll: "Faz scroll para explorar",
    },
    about: {
      overline: "Como penso",
      title: "Não é uma biografia. É uma forma de ver problemas.",
      lead: "Movo-me entre a sala de reuniões e o chão de fábrica. Onde a maioria vê um pedido de funcionalidade, eu vejo um processo à espera de ser repensado.",
      items: [
        { t: "Experiência Comercial", d: "Anos a construir pipelines, pricing e jornadas de cliente. Percebo como as empresas ganham dinheiro." },
        { t: "Manufatura Industrial", d: "Tempo dentro de linhas de produção, planeamento e qualidade. Sei onde se escondem os bottlenecks." },
        { t: "Software", d: "Execução full-stack. Entrego sistemas em produção, não apresentações." },
        { t: "Inteligência Artificial", d: "Cada problema começa com a pergunta: onde é que a IA cria alavancagem desproporcional?" },
        { t: "Estratégia de Negócio", d: "A tecnologia só é útil se mover um número que importa ao negócio." },
        { t: "Melhoria Contínua", d: "Pensamento lean. Eliminar desperdício, acumular ganhos, medir tudo." },
        { t: "Automação", d: "Se um humano o faz duas vezes da mesma forma, devia ser um sistema." },
        { t: "Design de Produto", d: "Interfaces que as pessoas querem mesmo usar, à volta de como o trabalho acontece." },
      ],
    },
    approach: {
      overline: "A minha abordagem",
      title: "Do caos operacional à clareza com IA.",
      steps: [
        { t: "Descobrir", d: "Entender o negócio, não o briefing." },
        { t: "Desafiar premissas", d: "O problema declarado raramente é o real." },
        { t: "Mapear operações", d: "Seguir cada passo, transição e atraso." },
        { t: "Desenhar fluxos de IA", d: "Encontrar onde a inteligência cria alavancagem." },
        { t: "Prototipar", d: "Software funcional em dias, não trimestres." },
        { t: "Implementar", d: "Lançar no mundo real, com utilizadores reais." },
        { t: "Medir", d: "Ligar tudo a lucro, velocidade, experiência." },
        { t: "Melhorar", d: "Iterar sem parar. O sistema fica mais inteligente." },
      ],
    },
    build: {
      overline: "O que construo",
      title: "Sistemas ponta-a-ponta que mudam como as empresas operam.",
      cards: [
        { t: "Gestão de Projetos para Developers e Construtores", d: "Planeia, acompanha e entrega projetos de equipas de desenvolvimento e construção.", k: "platform" },
        { t: "Manufatura Industrial", d: "Controlo de chão de fábrica, planeamento, qualidade.", k: "factory" },
        { t: "Gestão de Saúde", d: "Operações hospitalares, blocos operatórios, agendamento.", k: "health" },
        { t: "Armazém & Fulfilment", d: "Picking, packing, código de barras, expedição.", k: "warehouse" },
        { t: "Plataformas E-commerce", d: "Lojas ligadas a stock, pagamentos e operações.", k: "ecom" },
        { t: "Plataformas de Clubes", d: "Pais, academias, eventos, pagamentos, presenças.", k: "sports" },
        { t: "CRM & ERP Personalizado", d: "Extensões e sistemas à medida à volta do NetSuite.", k: "crm" },
        { t: "Agentes de IA & Automação", d: "Agentes que leem, decidem e agem na tua stack.", k: "agent" },
      ],
      integrations: ["NetSuite", "Shopify", "Stripe", "OpenAI", "Claude", "Gemini", "Google Workspace", "Microsoft"],
    },
    transformation: {
      overline: "Interativo",
      title: "Arrasta o teu negócio do caos para operações com IA.",
      sub: "Puxa o controlo. Vê o que acontece quando as operações deixam de reagir e começam a pensar.",
      chaos: "Caos",
      ai: "Operado por IA",
      before: [
        { l: "Entrada manual de dados", v: "14h / semana" },
        { l: "Erros de encomenda", v: "8.2%" },
        { l: "Latência de decisão", v: "3 dias" },
        { l: "Visibilidade", v: "Folhas de cálculo" },
      ],
      after: [
        { l: "Entrada manual de dados", v: "0.5h / semana" },
        { l: "Erros de encomenda", v: "0.4%" },
        { l: "Latência de decisão", v: "Tempo real" },
        { l: "Visibilidade", v: "Dashboard ao vivo" },
      ],
      dragHint: "Arrasta",
    },
    projects: {
      overline: "Projetos em destaque",
      title: "Sistemas realistas. Impacto operacional real.",
      open: "Explorar caso",
      close: "Fechar",
      challenge: "O desafio",
      solution: "O que construí",
      impact: "Impacto",
      list: [
        {
          name: "Suite de Operações Hospitalares",
          tagline: "Blocos operatórios, equipas e equipamento, orquestrados.",
          tags: ["Saúde", "Agendamento", "Tempo real"],
          challenge: "Um hospital privado perdia horas de bloco operatório com agendamentos em conflito, equipamento em falta e coordenação manual entre equipas.",
          solution: "Uma suite unificada para agendamento de blocos, escalas de pessoal, rastreio de equipamento e inventário, com deteção de conflitos e quadros de estado ao vivo.",
          features: ["Agendamento de blocos", "Gestão de turnos", "Rastreio de equipamento", "Inventário & esterilização", "Deteção de conflitos", "Quadros ao vivo"],
          impact: [{ l: "Utilização de bloco", v: "+27%" }, { l: "Conflitos", v: "-91%" }, { l: "Atrasos de setup", v: "-40%" }],
        },
        {
          name: "Plataforma Têxtil Industrial",
          tagline: "Da matéria-prima ao produto expedido, numa só vista.",
          tags: ["Manufatura", "Planeamento", "Qualidade"],
          challenge: "Um fabricante têxtil geria a produção em quadros e folhas de cálculo, sem visão em tempo real de capacidade, qualidade ou componentes.",
          solution: "Uma plataforma de gestão fabril cobrindo ordens de produção, planeamento de componentes, controlo de qualidade e dashboards de chão de fábrica.",
          features: ["Planeamento de produção", "Controlo de chão de fábrica", "Gestão de qualidade", "Rastreio de componentes & BOM", "Planeamento de capacidade", "Dashboards em tempo real"],
          impact: [{ l: "Entregas a horas", v: "+34%" }, { l: "Taxa de refugo", v: "-22%" }, { l: "Tempo de planeamento", v: "-70%" }],
        },
        {
          name: "Sistema de Gestão de Armazém",
          tagline: "Fulfilment que escala sem caos.",
          tags: ["Logística", "Fulfilment", "Integrações"],
          challenge: "Uma operação de e-commerce em crescimento bateu numa parede: erros de picking, packing lento e zero integração entre loja, stock e transportadoras.",
          solution: "Um WMS com picking por código de barras, validação de packing, automação de etiquetas e integrações profundas com Shopify e transportadoras.",
          features: ["Picking por código de barras", "Validação de packing", "Automação de expedição", "Sincronização de stock", "Packing lists", "Integração de transportadoras"],
          impact: [{ l: "Picks por hora", v: "+58%" }, { l: "Erros de picking", v: "-86%" }, { l: "Encomendas / dia", v: "3.2x" }],
        },
        {
          name: "Plataforma de Clube Desportivo",
          tagline: "Academias, pais e pagamentos num só lugar.",
          tags: ["Plataforma", "Pagamentos", "Comunidade"],
          challenge: "Uma academia multi-desporto geria inscrições, presenças, eventos e pagamentos de pais em ferramentas desconexas.",
          solution: "Uma plataforma para pais e academias cobrindo agendamento, presenças, eventos, loja online e pagamentos recorrentes.",
          features: ["Portal de pais", "Gestão de academias", "Agendamento de eventos", "Registo de presenças", "Loja online", "Pagamentos recorrentes"],
          impact: [{ l: "Tempo administrativo", v: "-65%" }, { l: "Cobrança", v: "+41%" }, { l: "NPS dos pais", v: "72" }],
        },
        {
          name: "Plataforma de Eventos & Espaços",
          tagline: "Reservas e operações, coreografadas.",
          tags: ["Operações", "Reservas", "Espaços"],
          challenge: "Um grupo de espaços geria reservas, recursos e operação do dia do evento por telefone e folhas em papel.",
          solution: "Uma plataforma de gestão de espaços que trata reservas, alocação de recursos e run-sheets operacionais ao vivo nos dias de evento.",
          features: ["Gestão de espaços", "Motor de reservas", "Alocação de recursos", "Run-sheets operacionais", "Controlo de capacidade", "Timelines ao vivo"],
          impact: [{ l: "Reservas duplicadas", v: "0" }, { l: "Erros de setup", v: "-78%" }, { l: "Receita / espaço", v: "+19%" }],
        },
      ],
    },
    different: {
      overline: "O que me torna diferente",
      title: "Não escrevo código. Identifico oportunidades.",
      cards: [
        { t: "Visão Comercial", d: "Entendo pipelines de vendas, modelos de negócio e jornadas de cliente — e construo para eles." },
        { t: "Experiência Industrial", d: "Anos dentro de manufatura e produção. Vi onde os sistemas partem no mundo real." },
        { t: "AI First", d: "Cada solução começa por perguntar onde a IA cria alavancagem, não onde parece impressionante." },
        { t: "Execução Rápida", d: "Ideias tornam-se software funcional em dias. O momentum é uma vantagem competitiva." },
        { t: "Pensamento de Negócio", d: "O software só é útil se melhorar lucro, velocidade ou experiência do cliente." },
      ],
    },
    tech: {
      overline: "Tecnologia",
      title: "A stack que orquestro.",
      sub: "As ferramentas são meios, não fins — escolhidas para o problema.",
      items: ["Claude", "OpenAI", "Gemini", "Supabase", "Postgres", "Next.js", "React", "Node", "TypeScript", "Python", "Stripe", "Shopify", "NetSuite", "Google Cloud", "Vercel", "WebSockets", "Realtime", "APIs", "OCR", "LLMs", "Automação"],
    },
    stats: {
      overline: "Em números",
      title: "Resultados, não outputs.",
      items: [
        { v: 36, suffix: "", l: "Projetos entregues" },
        { v: 11, suffix: "", l: "Negócios melhorados" },
        { v: 80, suffix: "+", l: "Módulos Odoo" },
        { v: 210, suffix: "+", l: "Processos automatizados" },
        { v: 3, suffix: "", l: "Países" },
        { v: 7, suffix: "", l: "Indústrias" },
      ],
    },
    testimonials: {
      overline: "Testemunhos",
      title: "O que dizem os parceiros.",
      list: [
        { q: "O Rui não construiu o que pedimos. Construiu o que realmente precisávamos — e as nossas operações nunca mais foram as mesmas.", n: "Diretor de Operações", r: "Manufatura Industrial" },
        { q: "Em duas semanas entendeu o nosso negócio melhor que fornecedores com quem trabalhámos durante anos. O sistema pagou-se num trimestre.", n: "CEO", r: "Grupo de Saúde" },
        { q: "A maioria dos developers entrega funcionalidades. O Rui entrega resultados. Pensa como dono, não como prestador.", n: "Fundador", r: "Plataforma E-commerce" },
        { q: "Andou pelo nosso chão de fábrica antes de escrever uma linha de código. Foi por isso que funcionou.", n: "Diretor de Fábrica", r: "Produção Têxtil" },
      ],
    },
    contact: {
      overline: "Contacto",
      line1: "Se procuras mais uma empresa de software, provavelmente não sou a escolha certa.",
      line2: "Se procuras alguém que vai repensar o teu negócio de raiz, vamos falar.",
      cta: "Marcar uma Discovery Call",
      email: "Ou envia-me um email direto",
      waMsg: "Olá Rui, gostava de falar sobre um projeto.",
    },
    footer: {
      tagline: "Consultor de negócio com IA. Repenso como as empresas operam.",
      rights: "Todos os direitos reservados.",
      built: "Desenhado e construído com intenção.",
    },
  },
};

export const CONTACT = {
  email: "me@ruiguimaraes.dev",
  linkedin: "https://www.linkedin.com/in/rui-guimar%C3%A3es-5784ba88/",
  github: "https://github.com/ruiguimaraes",
  whatsapp: "https://wa.me/351915195192",
  domain: "ruiguimaraes.dev",
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("rg-lang") || "en");
  useEffect(() => { localStorage.setItem("rg-lang", lang); }, [lang]);
  const t = translations[lang];
  const toggle = () => setLang((l) => (l === "en" ? "pt" : "en"));
  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
